import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormCombos } from "../../../../hooks/useFormCombos";
import { getCommittees } from "../../../../services/committees.service";
import { getCompaniesByUser } from "../../../../services/companies.service";
import {
  finalDeliberations,
  getMembersByName,
} from "../../../../services/formCombos.service.js";
import {
  getInputDateFormats
} from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";
import Textarea from "../../../Common/Textarea";
import {
  ButtonsContainer,
  Content,
  FieldsContainer,
  HeaderContainer,
  OpenedInputDescription,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  chip: {
    margin: 2,
  },
}));

function FilterDrawer({ onClose, t, actionShowSpinner, setFilters, filters }) {
  const classes = useStyles();
  const { i18n } = useTranslation();

  const [committeesOptions, setCommitteesOptions] = useState([]);

  const [presenterInputValue, setPresenterInputValue] = useState("");
  const [finalDeliberationOptions, setFinalDeliberationOptions] = useState([]);
  const [presenters, setPresenters] = useState([]);
  const [selectedPresenter, setSelectedPresenter] = useState();
  const [requesterInputValue, setRequesterInputValue] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState(filters.companies);
  const [requesters, setRequesters] = useState([]);
  const [selectedRequester, setSelectedRequester] = useState();
  const [directory, setDirectory] = useState();
  const [committees, setCommittees] = useState([]);
  const [modality, setModality] = useState();
  const [meetStartDate, setMeetStartDate] = useState(null);
  const [finalDeliberationStatus, setFinalDeliberationStatus] = useState([]);
  const [meetEndDate, setMeetEndDate] = useState(null);
  const [openedField, setOpenedField] = useState("");
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const {
    getDirectors,
    directorsList,
    sanitizeDirectorsForSelect,
    modalityList,
    getAllModalityByLanguageId,
    sanitizeForTable,
  } = useFormCombos();

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");

    const loadCommitteeOptions = async () => {
      const response = await getCommittees({
        showDisabled: true,
        pageSize: 999999
      });
      
      setCommitteesOptions(
        response.data.items
      );
    };
    const loadCompaniesOptions = async () => {
      const companies = await getCompaniesByUser();

      setCompaniesOptions(companies);
    };

    actionShowSpinner(true);

    loadCommitteeOptions();
    loadCompaniesOptions();

    getDirectors(language);
    getAllModalityByLanguageId(language, false);

    setSelectedRequester(filters?.requester);
    setSelectedPresenter(filters?.presenter);
    setDirectory(filters?.directory);
    setFinalDeliberationStatus(filters?.finalDeliberationStatus);
    setCommittees(filters?.committees);
    setModality(filters?.modality);
    setMeetStartDate(filters?.startDate);
    setMeetEndDate(filters?.endDate);
    setOpenedField(filters?.keyword);

    actionShowSpinner(false);
  }, [filters]);

  useEffect(() => {
    async function load() {
      if (requesterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", requesterInputValue);

        if (res.error?.length > 0) {
          setRequesters([]);
        }

        setRequesters(res.data);
      } else {
        setRequesters([]);
      }

      if (presenterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", presenterInputValue);

        if (res.error?.length > 0) {
          setPresenters([]);
        }

        setPresenters(res.data);
      } else {
        setPresenters([]);
      }
    }

    load();
  }, [requesterInputValue, presenterInputValue]);

  useEffect(() => {
    setFinalDeliberationOptions(finalDeliberations);
  }, [finalDeliberations]);

  const handleApplyFilters = () => {
    setFilters({
      requester: selectedRequester,
      presenter: selectedPresenter,
      directory: directory,
      finalDeliberationStatus: finalDeliberationStatus,
      committees: committees,
      ...(selectedCompanies && { companies: selectedCompanies }),
      modality: modality,
      ...(meetStartDate ? { startDate: meetStartDate } : { startDate: null }),
      ...(meetEndDate ? { endDate: meetEndDate } : { endDate: null }),
      keyword: openedField,
    });

    onClose();
  };

  const handleCleanFilters = () => {
    setFilters({
      requester: undefined,
      presenter: undefined,
      directory: undefined,
      finalDeliberationStatus: [],
      committees: [],
      companies: [],
      modality: undefined,
      startDate: null,
      endDate: null,
      keyword: "",
    });

    onClose();
  };

  return (
    <Box sx={{ width: 460, flex: 1, display: "flex" }} role="presentation">
      <Content>
        <HeaderContainer>
          <Typography variant="h4">{t("forms.filters")}</Typography>

          <Close
            onClick={() => {
              onClose();
            }}
          />
        </HeaderContainer>

        <FieldsContainer>
          <Grid container spacing={3}>
            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="requester"
                options={requesters}
                value={selectedRequester}
                onInputChange={(_event, newInputValue, _prop) => {
                  setRequesterInputValue(newInputValue);
                }}
                inputValue={requesterInputValue}
                label={t("forms.minutes.actions.requesterName")}
                setValue={(value) => {
                  setSelectedRequester(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="presenter"
                options={presenters}
                value={selectedPresenter}
                onInputChange={(_event, newInputValue, _prop) => {
                  setPresenterInputValue(newInputValue);
                }}
                inputValue={presenterInputValue}
                label={t("slots.presenter")}
                setValue={(value) => {
                  setSelectedPresenter(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="directories"
                options={sanitizeDirectorsForSelect(directorsList)}
                value={directory}
                label={t("slots.board")}
                setValue={(value) => {
                  setDirectory(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="modality"
                options={sanitizeForTable(modalityList)}
                value={modality}
                label={t("slots.modality")}
                setValue={(value) => {
                  setModality(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="deliberation"
                options={finalDeliberationOptions}
                value={finalDeliberationStatus}
                label={t("forms.minutes.actions.finalDeliberation")}
                setValue={(values) => {
                  setFinalDeliberationStatus(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setFinalDeliberationStatus(
                          finalDeliberationStatus.filter(
                            (v) => v.id !== option.id
                          )
                        );
                      }}
                      onClick={() => {
                        setFinalDeliberationStatus(
                          finalDeliberationStatus.filter(
                            (v) => v.id !== option.id
                          )
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="companies"
                options={
                  companiesOptions?.map((item) => ({
                    id: item.companyId,
                    name: `${item.companyName} - ${item.companyAbbreviation}`,
                  })) ?? []
                }
                getOptionSelected={(option, value) => option.id === value.id}
                value={selectedCompanies}
                label={t("slots.companies")}
                setValue={(values) => {
                  setSelectedCompanies(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="committees"
                options={committeesOptions ? committeesOptions : []}
                value={committees}
                label={t("committees")}
                setValue={(values) => {
                  setCommittees(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setCommittees(
                          committees.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setCommittees(
                          committees.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.startDateLabel")}
                value={meetStartDate}
                onChange={(date) => {
                  setMeetStartDate(date ? startOfDay(date) : null);
                }}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.endDateLabel")}
                value={meetEndDate}
                onChange={(date) => {
                  setMeetEndDate(date ? startOfDay(date) : null);
                }}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Textarea
                name="freeField"
                id="freeField"
                onChange={(e) => setOpenedField(e.target.value)}
                value={openedField}
                style={{ width: "100%" }}
                label={t("forms.openedField")}
              />

              <OpenedInputDescription>
                {t("forms.deliberationsFreeInput")}
              </OpenedInputDescription>
            </Grid>
          </Grid>
        </FieldsContainer>

        <ButtonsContainer>
          <Button
            variant="contained"
            onClick={handleCleanFilters}
            className={classes.buttonOutlined}
          >
            {t("forms.cleanFilters")}
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            className={classes.button}
          >
            {t("forms.applyFilters")}
          </Button>
        </ButtonsContainer>
      </Content>
    </Box>
  );
}

export default FilterDrawer;
